import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector, shallowEqual} from 'react-redux';
import {useFormatMessage, usePrevious} from 'hooks';
import { Redirect, Link } from 'react-router-dom';
import classNames from "classnames";
import ClipLoader from "react-spinners/ClipLoader";
import RemoteTable from 'components/RemoteTable';
import paths from 'pages/Router/paths';
import ConfirmationModal from 'components/ConfirmationModal';
import { paginateAlbums, deleteAlbum, albumsCleanUp } from 'state/actions/albums';
import classes from "../Users/Users.module.scss";
import {getImgThumb} from "../../utils";
import { paginateSongs } from '../../state/actions/songs';
import {createFeatured, deleteFeatured} from "../../state/actions/featured";

const Albums = () => {
    const {albumsList, albumsTotal, page, isAdmin, error, loading, deleted} = useSelector(
        (state) => {
            return ({
                albumsList: state.albums.data,
                albumsTotal: state.albums.total,
                page: state.albums.page,
                isAdmin: state.auth.userData.isAdmin,
                error: state.albums.error,
                loading: state.albums.loading,
                deleted: state.albums.deleted,
            });
        },
        shallowEqual
    );

    const [deleteModal, setDeleteModal] = useState({
        albumId: null,
        isOpen: false,
    });
    const dispatch = useDispatch();
    const [search, setSearch] = useState('');
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [pageSize, setPageSize] = useState(50);
    const prev = usePrevious({page, pageSize, search});

    const handlePageChange = (newPage, movement) => {
        dispatch(paginateAlbums(pageSize, search, movement));
    };

    const handleSort = (attribute) => {
        if(attribute) {
            dispatch(paginateAlbums(pageSize, search, 'init', attribute));
        }
    };

    const pullInitial = () => dispatch(paginateAlbums(pageSize, search, 'init', 'createdAt'));

    useEffect(() => {
        if (isAdmin) {
            pullInitial();
        }

        return () => dispatch(albumsCleanUp());
    }, [dispatch, isAdmin]);

    useEffect(() => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        if(prev === undefined) {
            return; // Only run once we have actually searched
        }

        const timeout = setTimeout(() => {
            pullInitial();
        }, 500);
        setSearchTimeout(timeout);
    }, [search]);

    useEffect(() => {
        if (prev && pageSize !== prev.pageSize) {
            pullInitial();
        }
    }, [pageSize]);

    useEffect(() => {
        if (deleted && !loading) {
            setDeleteModal((prevState) => ({
                albumId: null,
                isOpen: !prevState.isOpen,
            }));
        }
    }, [deleted, loading]);

    const redirect = !isAdmin && <Redirect to={paths.ROOT}/>;

    const onRemoveButtonClickHandler = (albumId) => {
        setDeleteModal((prevState) => ({
            albumId,
            isOpen: !prevState.isOpen,
        }));
    };

    const onCloseModalHandler = () => {
        setDeleteModal({albumId: null, isOpen: false});
    };

    const onDeleteAlbumHandler = () => {
        dispatch(deleteAlbum(deleteModal.albumId));
    };

    const onHandleFeatured = (album) => {
        const data = {
            ...album,
            type: 'Album'
        };
        const createFeature = !album.featuredId;
        if(createFeature) {

            dispatch(createFeatured(data));
        } else {
            dispatch(deleteFeatured(data));
        }
        pullInitial();
    };

    const cols = [
        {
            name: '',
        },
        {
            name: 'Name',
            field: 'name',
        },
        {
            name: 'Year',
            field: 'recordingYear',
        },
        {
            name: 'Songs',
        },
        {
            name: 'Created',
            field: 'createdAt',
        },
        {
            name: '',
        },
    ];

    const tableData = albumsList.map((album) => {
        return [
            {
                className: 'table-img-cell',
                value: (<>
                    <img src={`${getImgThumb(album.img, true)}`} alt="" className="table-img album-img" />
                </>),
            },
            {
                value: album.name,
            },
            {
                value: album.recordingYear,
            },
            {
                value: album.songs?.length ?? 0,
            },
            {
                value: new Date(album.createdAt?.seconds * 1000).toLocaleDateString(),
            },
            {
                className: 'is-actions-cell',
                value: (<>
                    <div className="buttons is-right">
                        <button
                            type="button"
                            className="button is-small"
                            onClick={() => onHandleFeatured(album)}
                        >
                                <span className="icon is-small">
                                  <i className={album.featuredId ? "mdi mdi-star" : "mdi mdi-star-outline"}/>
                                </span>
                        </button>
                        <Link to={`/albums/${album.id}`} className="button is-small is-primary">
                            <span className="icon is-small">
                              <i className="mdi mdi-pencil-outline"/>
                            </span>
                        </Link>
                        <button
                            type="button"
                            className="button is-small is-danger"
                            onClick={() => onRemoveButtonClickHandler(album.id)}
                        >
                            <span className="icon is-small">
                              <i className="mdi mdi-trash-can"/>
                            </span>
                        </button>
                    </div>
                </>),
            },
        ];
    });

    const deleteMessage = useFormatMessage('Albums.delete');
    const confirmMessage = useFormatMessage('Albums.confirm');
    const permDeleteMessage = useFormatMessage('Albums.permDelete');
    const cancelMessage = useFormatMessage('Albums.cancel');

    return (
        <>
            {redirect}
            {deleteModal.isOpen && (
                <ConfirmationModal
                    isActive={deleteModal.isOpen}
                    isLoading={loading}
                    confirmButtonMessage={deleteMessage}
                    title={confirmMessage}
                    body={permDeleteMessage}
                    cancelButtonMessage={cancelMessage}
                    onConfirmation={onDeleteAlbumHandler}
                    onCancel={onCloseModalHandler}
                />
            )}
            <section className="hero is-hero-bar">
                <div className="hero-body">
                    <div className="level">
                        <div className="level-left">
                            <div className="level-item">
                                <h1 className="title">{useFormatMessage('Albums.albums')} ({albumsTotal})</h1>
                            </div>
                        </div>
                        <div className="level-right">
                            <div className="level-item">
                                <Link to={paths.ADD_ALBUM} className="button">
                                    {useFormatMessage('Albums.newAlbum')}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className="section is-main-section">
                <div className="card has-table has-mobile-sort-spaced">
                    <header className="card-header">
                        <p className={classNames('card-header-title', classes.tableHeader)}>
                            <span>{useFormatMessage('Albums.search')}</span>
                            <input
                                type="text"
                                className="input"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </p>
                    </header>
                    <div className="b-table">
                        {loading ? <ClipLoader /> : <RemoteTable
                            columns={cols}
                            data={tableData}
                            onPageSizeChange={(size) => setPageSize(size)}
                            onPageChange={(pageNo, movementType) => handlePageChange(pageNo, movementType)}
                            onSort={(attribute) => handleSort(attribute)}
                            pages={Math.ceil(albumsTotal / pageSize)}
                            page={page}
                            pageSize={pageSize}
                        />}
                        {error && 'Show error'}
                    </div>
                </div>
            </section>
        </>
    );
};

export default Albums;
